/**
 * Configuration service for accessing application configuration
 * 
 * Runtime Configuration (Production):
 * - Configuration is fetched from /api/config endpoint at app startup
 * - Backend serves config from ECS-injected environment variables
 * - Same Docker image works in all environments (dev, staging, production)
 * 
 * Build-Time Configuration (Local Development):
 * - Dev auth mode bypasses OIDC configuration entirely
 * - VITE_API_URL configures Vite proxy target (vite.config.ts, not browser code)
 * - Browser code always uses relative URLs (/api/*) in both dev and prod
 */

import { isDevAuthMode } from '@/utils/env'

/**
 * Runtime configuration interface
 * Fetched from /api/config endpoint at app startup
 */
export interface RuntimeConfig {
  application: {
    name: string
    brand: string
    environment: string
    region: string
  }
  oidc: {
    authority: string
    client_id: string
    redirect_uri: string
    post_logout_redirect_uri: string
    scope: string
  }
  cognito?: {
    logout_domain: string
    client_id: string
  }
  monitoring?: {
    provider: string
    config: unknown
  }
  /**
   * Pendo product analytics — the agent key only. Absent when the backend's
   * `createPendoConfigFromEnv()` (@revalize/pendo) returns null, which is the
   * signal to skip loading the agent entirely. `apiUrl`/`contentDomain` are
   * deliberately NOT exposed here; they stay server-side for CSP.
   */
  pendo?: {
    apiKey: string
  }
  imageTag?: string
  /**
   * Stage 5 catalog media delivery (CAT-002). Optional so older backend
   * deployments without the CDN block continue to parse — frontend treats
   * missing config as "use the dev fallback".
   */
  media?: {
    cdnBase: string | null
    cdnContainer: string
    cdnExt: string
  }
}

/**
 * Frontend-facing media configuration. Always has all three fields filled —
 * the loader applies defaults when the backend omits the block.
 */
export interface MediaConfig {
  cdnBase: string | null
  cdnContainer: string
  cdnExt: string
}

/**
 * OIDC configuration interface with standard properties
 * Matches react-oidc-context AuthProviderProps exactly
 */
export interface OidcConfig {
  /** OIDC authority URL (identity provider endpoint) */
  authority: string
  /** OAuth 2.0 client identifier */
  client_id: string
  /** OAuth 2.0 redirect URI after authentication */
  redirect_uri: string
  /** OAuth 2.0 response type */
  response_type: 'code'
  /** OAuth 2.0 scopes */
  scope: string
}

/**
 * Cognito-specific extensions interface
 * Non-standard properties specific to AWS Cognito
 * Property names match AuthProvider expectations
 */
export interface CognitoExtensions {
  /** Cognito logout URL (e.g., https://domain.auth.region.amazoncognito.com) */
  cognitoLogoutUrl: string
  /** OAuth 2.0 client identifier (same as OIDC client_id) */
  cognitoClientId: string
}

/**
 * Service for managing application configuration
 */
export class ConfigService {
  private static config: RuntimeConfig | null = null
  private static initPromise: Promise<void> | null = null

  /**
   * Initialize configuration by fetching from /api/config
   * Call once at app startup before rendering React
   * Idempotent: subsequent calls return cached promise
   * 
   * @throws Error if fetch fails or validation fails
   */
  static async initialize(): Promise<void> {
    // Return cached promise if already initializing/initialized
    if (this.initPromise) {
      return this.initPromise
    }

    // Create and cache initialization promise.
    // Always fetch /api/config — even in dev-auth mode — so non-auth
    // config blocks (media CDN, monitoring, image tag) are available.
    // The OIDC block is only validated in non-dev mode because the backend
    // intentionally provides a placeholder OIDC config in dev mode (the
    // real auth is handled by DevAuthProvider instead).
    this.initPromise = (async (): Promise<void> => {
      try {
        const response = await fetch('/api/config')

        if (!response.ok) {
          throw new Error(
            `Failed to fetch /api/config: ${response.status} ${response.statusText}`
          )
        }

        const config: RuntimeConfig = await response.json()

        // Validate OIDC structure only in non-dev mode.
        // In dev-auth mode the backend supplies placeholder OIDC values
        // so this check would still pass — but skip it explicitly for clarity.
        if (!this.isDevAuthMode()) {
          if (!config.oidc || !config.oidc.authority || !config.oidc.client_id) {
            throw new Error('Invalid runtime config: missing required OIDC properties')
          }
        }

        this.config = config
      } catch (error) {
        // Clear promise on error to allow retry
        this.initPromise = null
        throw error
      }
    })()

    await this.initPromise
  }

  /**
   * Get OIDC config for AuthProvider
   * Returns null in dev mode or if not initialized
   * 
   * @returns OidcConfig for react-oidc-context AuthProvider
   */
  static getOidcConfig(): OidcConfig | null {
    // Dev mode bypass
    if (this.isDevAuthMode()) {
      return null
    }
    
    // Not initialized yet
    if (!this.config) {
      return null
    }
    
    // Transform RuntimeConfig.oidc to OidcConfig for AuthProvider
    return {
      authority: this.config.oidc.authority,
      client_id: this.config.oidc.client_id,
      redirect_uri: this.config.oidc.redirect_uri,
      response_type: 'code',  // Fixed value for AuthProvider
      scope: this.config.oidc.scope
    }
  }

  /**
   * Get Cognito-specific extensions
   * Returns null in dev mode or if cognito config not configured
   * 
   * @returns CognitoExtensions with cognitoLogoutUrl and cognitoClientId
   */
  static getCognitoExtensions(): CognitoExtensions | null {
    // Dev mode bypass
    if (this.isDevAuthMode()) {
      return null
    }
    
    // Not initialized or no cognito config
    if (!this.config?.cognito) {
      return null
    }
    
    // Map backend property names to AuthProvider property names
    return {
      cognitoLogoutUrl: this.config.cognito.logout_domain,
      cognitoClientId: this.config.cognito.client_id
    }
  }

  /**
   * Checks if the application is running in development authentication mode
   * @returns True if VITE_DEV_AUTH_MODE is set to 'true'
   */
  static isDevAuthMode(): boolean {
    return isDevAuthMode()
  }

  /**
   * Get monitoring configuration for error tracking and logging
   * Returns null if not configured
   * 
   * @returns Monitoring config object with provider and config, or null
   */
  static getMonitoringConfig(): { provider: string; config: unknown } | null {
    return this.config?.monitoring || null
  }

  /**
   * Gets the deployment environment (dev/stg/prd)
   * Returns 'dev' as fallback for local dev mode
   */
  static getEnvironment(): string {
    return this.config?.application.environment || 'dev'
  }

  /**
   * Get Pendo analytics configuration.
   *
   * Returns null when the backend omitted the block (Pendo unconfigured), which
   * `PendoRoot` treats as "analytics disabled" and answers with
   * `NoopPendoProvider`. Note this gates on the *backend-supplied* config
   * rather than Vite's build-time `MODE`: one image serves dev/stg/prd, so
   * build mode says nothing about the environment.
   *
   * @returns Pendo config with the agent API key, or null
   */
  static getPendoConfig(): { apiKey: string } | null {
    const apiKey = this.config?.pendo?.apiKey
    return apiKey ? { apiKey } : null
  }

  /**
   * Gets the application brand (used as Pendo account metadata)
   */
  static getBrand(): string {
    return this.config?.application.brand || 'unknown'
  }

  /**
   * Gets the deployment region (used as Pendo account metadata)
   */
  static getRegion(): string {
    return this.config?.application.region || 'unknown'
  }

  /**
   * Gets the image tag for release tracking
   * Returns 'dev-local' as fallback for local dev mode
   */
  static getImageTag(): string {
    return this.config?.imageTag || 'dev-local'
  }

  /**
   * Get media CDN configuration (Stage 5 catalog images, CAT-002).
   *
   * Returns sensible defaults when the backend hasn't loaded the media block
   * yet — `cdnBase = null` triggers the dev fallback (`/api/media/{id}/download`)
   * in `getMediaAssetUrl`.
   */
  static getMediaConfig(): MediaConfig {
    const m = this.config?.media
    return {
      cdnBase: m?.cdnBase ?? null,
      cdnContainer: m?.cdnContainer ?? 'media-assets',
      cdnExt: m?.cdnExt ?? 'webp',
    }
  }

  /**
   * Gets the API base URL for browser requests
   * 
   * Always returns '/api' (relative URL) because:
   * - Development: Vite proxy intercepts /api/* and forwards to backend
   * - Production: Frontend served by backend, /api/* goes to same origin
   * 
   * Note: VITE_API_URL is used by vite.config.ts (Node.js proxy target),
   * NOT by browser code. Browser code should always use relative URLs.
   * 
   * @returns API base URL (always '/api')
   */
  static getApiBaseUrl(): string {
    return '/api'
  }
}
